/**
 * The HTTP layer.
 *
 * Everything the API returns is wrapped in { success, data } or
 * { success, error }, so `request` unwraps that in one place and throws a
 * typed ApiError otherwise — callers only ever deal with the payload.
 *
 * Access tokens are short-lived. When one expires mid-flight the request is
 * retried once behind a single shared refresh, so a screen making three
 * parallel calls refreshes once rather than three times (and never races
 * itself into a logged-out state).
 */
import { API_BASE, apiUrl } from "./config";
import { readSession, writeSession, clearSession } from "../lib/session";

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(message: string, code = "error", status = 0, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
}

/** FastAPI's own error shape, which any framework-level failure returns. */
interface FastApiError {
  detail?: unknown;
}

interface FieldError {
  field?: string;
  message?: string;
}

/**
 * A human-readable summary of a 422.
 *
 * The API returns one entry per bad field, so name the field rather than
 * saying "some of the details you sent aren't valid" and leaving the person
 * to guess which.
 */
function describeFieldErrors(details: unknown): string | null {
  if (!Array.isArray(details) || details.length === 0) return null;
  const parts = (details as FieldError[])
    .filter((entry) => entry?.message)
    .map((entry) => {
      const label = entry.field ? prettyFieldName(entry.field) : null;
      return label ? `${label}: ${entry.message}` : entry.message;
    });
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0] as string;
  return parts.slice(0, 3).join(" · ");
}

function prettyFieldName(field: string): string {
  const leaf = field.split(".").pop() ?? field;
  const words = leaf.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Turn a failed response into something worth reading.
 *
 * Three sources, in order of usefulness:
 *   1. our own envelope — already written for a person
 *   2. FastAPI's `detail` — means the request never reached our code, which
 *      in practice means the wrong service, the wrong path, or no service
 *   3. the bare status code
 */
function toApiError(status: number, path: string, payload: unknown): ApiError {
  const envelope = payload as Envelope<unknown> | null;

  if (envelope?.error?.message) {
    const summary =
      status === 422 ? describeFieldErrors(envelope.error.details) : null;
    return new ApiError(
      summary ?? envelope.error.message,
      envelope.error.code,
      status,
      envelope.error.details,
    );
  }

  // Anything 5xx that did not come through our own envelope is an internal
  // fault. Its `detail` ("Internal Server Error", a stack trace, a proxy's
  // HTML) is for us, not for the person reading it.
  if (status >= 500) {
    return new ApiError(
      "Something went wrong on our side. Please try again in a moment.",
      "server_error",
      status,
    );
  }

  // A 404 here is almost never "this record doesn't exist" — our own handlers
  // return the envelope for that. It means nothing is serving this route: the
  // API is down, or another service is on that port.
  if (status === 404) {
    return new ApiError(
      `The contributor API isn't serving ${path}. Check that it's running at ` +
        `${API_BASE} — another service may be on that port.`,
      "api_route_not_found",
      status,
    );
  }

  const fastApi = payload as FastApiError | null;
  if (fastApi?.detail !== undefined && fastApi.detail !== null) {
    const detail =
      typeof fastApi.detail === "string"
        ? fastApi.detail
        : (describeFieldErrors(fastApi.detail) ?? "That request wasn't accepted.");
    return new ApiError(detail, "api_error", status);
  }

  if (status === 401) {
    return new ApiError("Your session has expired. Please sign in again.", "unauthorized", status);
  }
  if (status === 413) {
    return new ApiError("That recording is too large to upload.", "too_large", status);
  }
  if (status === 429) {
    return new ApiError("Too many requests. Wait a moment and try again.", "rate_limited", status);
  }
  return new ApiError(`That request didn't go through (${status}).`, "error", status);
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send the contributor's access token. Default true. */
  auth?: boolean;
  /** Back-office calls send this instead of a contributor token. */
  adminKey?: string;
  /** Reviewer calls send a platform token instead. */
  bearer?: string;
  signal?: AbortSignal;
  /** Already-encoded body (FormData); skips JSON serialisation. */
  formData?: FormData;
}

let refreshInFlight: Promise<boolean> | null = null;

/** Session-expiry hook, wired up by the auth store so it can route to /login. */
let onUnauthorised: (() => void) | null = null;
export function setUnauthorisedHandler(handler: () => void) {
  onUnauthorised = handler;
}

async function refreshAccessToken(): Promise<boolean> {
  const session = readSession();
  if (!session?.refresh_token) return false;

  // One refresh at a time, however many callers arrive at once.
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(apiUrl("/auth/refresh"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: session.refresh_token }),
        });
        const payload = (await res.json()) as Envelope<{
          access_token: string;
          refresh_token: string;
          expires_in: number;
        }>;
        if (!res.ok || !payload.success || !payload.data) return false;
        writeSession({
          access_token: payload.data.access_token,
          refresh_token: payload.data.refresh_token,
        });
        return true;
      } catch {
        return false;
      } finally {
        // Cleared on the next tick so everyone awaiting this attempt shares it.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

async function send<T>(path: string, options: RequestOptions, retrying = false): Promise<T> {
  const { method = "GET", body, auth = true, adminKey, bearer, signal, formData } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (!formData && body !== undefined) headers["Content-Type"] = "application/json";

  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`;
  } else if (adminKey) {
    headers["X-Admin-Key"] = adminKey;
  } else if (auth) {
    const token = readSession()?.access_token;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers,
      signal,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    // fetch only rejects on a transport failure: offline, DNS, refused
    // connection, CORS. A running API that dislikes the request still
    // resolves, so this really does mean "nothing answered".
    throw new ApiError(
      `Can't reach the contributor API at ${API_BASE}. Check your connection, ` +
        `and that the API is running.`,
      "network_error",
    );
  }

  let payload: Envelope<T> | null = null;
  try {
    payload = (await res.json()) as Envelope<T>;
  } catch {
    // A non-JSON body (an nginx error page, a proxy timeout) — the status
    // code is all we have to go on.
    payload = null;
  }

  if (res.status === 401 && auth && !bearer && !adminKey && !retrying) {
    if (await refreshAccessToken()) return send<T>(path, options, true);
    clearSession();
    onUnauthorised?.();
    throw new ApiError("Your session has expired. Please sign in again.", "session_expired", 401);
  }

  if (!res.ok || !payload?.success) {
    throw toApiError(res.status, path, payload);
  }

  return payload.data as T;
}

export const api = {
  get: <T>(path: string, options: RequestOptions = {}) => send<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    send<T>(path, { ...options, method: "POST", body }),
  /** Whole-resource replace. Payout details are sent this way on purpose:
   *  they are saved as a set, never merged field by field. */
  put: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    send<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    send<T>(path, { ...options, method: "PATCH", body }),
  upload: <T>(path: string, formData: FormData, options: RequestOptions = {}) =>
    send<T>(path, { ...options, method: "POST", formData }),
};
